import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Ticket, Percent, Euro, Plus, Trash2, Pencil, UserPlus, Send, Wallet, Store, Users, BellRing } from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";
import WaSendQueue from "../components/WaSendQueue";
import AiHelper from "../components/AiHelper";
import AiImage from "../components/AiImage";
import DatePickerInput from "../components/DatePickerInput";
import { resolveImageUrl } from "../context/AppSettingsContext";
import { Upload, Image as ImageIcon } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

export default function Coupons() {
  const imageInputRef = useRef(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [segments, setSegments] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [assignBranchFilter, setAssignBranchFilter] = useState("__all__");
  const [assignSegmentFilter, setAssignSegmentFilter] = useState("__all__");
  const [segmentMemberIds, setSegmentMemberIds] = useState([]);
  const [broadcastFor, setBroadcastFor] = useState(null);
  const [broadcastBody, setBroadcastBody] = useState("");
  const [broadcastPreview, setBroadcastPreview] = useState(null);
  const [broadcastPreviewLoading, setBroadcastPreviewLoading] = useState(false);
  const [waQueue, setWaQueue] = useState(null); // {messages: [...]}
  const empty = {
    name: "", code: "", discount_type: "percent", discount_value: 10,
    min_spend: 0, valid_from: today(), valid_until: "", active: true, single_use: true,
    wallet_only: false,
    image_url: "",
    allowed_weekdays: [],
    allowed_hour_start: null,
    allowed_hour_end: null,
    branch_ids: [],
  };
  const [form, setForm] = useState(empty);
  const [reminderBusy, setReminderBusy] = useState(false);
  const runExpiryReminder = async () => {
    setReminderBusy(true);
    try {
      const { data } = await api.post("/segments/expiring-tomorrow/run-now");
      const d = `${data.date.slice(8, 10)}/${data.date.slice(5, 7)}`;
      if (!data.coupons) toast.info(`Nessun coupon scade domani (${d}).`);
      else if (!data.customers) toast.info(`${data.coupons} coupon ${data.coupons === 1 ? "scade" : "scadono"} domani (${d}), ma i clienti sono già stati avvisati.`);
      else toast.success(`Promemoria inviati: ${data.customers} clienti · ${data.pushed} push · ${data.whatsapp} WhatsApp (coupon in scadenza ${d}: ${data.coupons})`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setReminderBusy(false); }
  };

  const load = async () => {
    const [c, cu, b, s] = await Promise.all([
      api.get("/coupons"),
      api.get("/customers"),
      api.get("/branches").catch(() => ({ data: [] })),
      api.get("/segments").catch(() => ({ data: [] })),
    ]);
    setCoupons(c.data);
    setCustomers(cu.data);
    setBranches(b.data);
    setSegments(s.data);
  };
  useEffect(() => { load(); }, []);

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "sede";

  const submit = async () => {
    try {
      const payload = { ...form };
      if (!payload.allowed_weekdays || payload.allowed_weekdays.length === 0) payload.allowed_weekdays = null;
      if (payload.allowed_hour_start === "" || payload.allowed_hour_start === null) payload.allowed_hour_start = null;
      if (payload.allowed_hour_end === "" || payload.allowed_hour_end === null) payload.allowed_hour_end = null;
      if (editing) await api.patch(`/coupons/${editing.id}`, payload);
      else await api.post("/coupons", payload);
      setOpen(false); toast.success("Coupon salvato"); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare?")) return;
    await api.delete(`/coupons/${id}`); toast.success("Eliminato"); load();
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      ...empty,
      ...c,
      allowed_weekdays: c.allowed_weekdays || [],
      branch_ids: c.branch_ids || [],
    });
    setOpen(true);
  };
  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };

  const toggleWeekday = (d) => setForm((prev) => {
    const cur = new Set(prev.allowed_weekdays || []);
    if (cur.has(d)) cur.delete(d); else cur.add(d);
    return { ...prev, allowed_weekdays: [...cur].sort() };
  });
  const toggleBranch = (bid) => setForm((prev) => {
    const cur = new Set(prev.branch_ids || []);
    if (cur.has(bid)) cur.delete(bid); else cur.add(bid);
    return { ...prev, branch_ids: [...cur] };
  });

  const openAssign = (c) => {
    setAssigning(c);
    setSelectedIds([]);
    // Pre-seed branch filter: if the coupon is restricted, pin the filter to the first allowed branch
    const restricted = (c.branch_ids || []).length > 0;
    setAssignBranchFilter(restricted ? c.branch_ids[0] : "__all__");
    setAssignSegmentFilter("__all__");
    setSegmentMemberIds([]);
  };

  // When segment filter changes, fetch its members so we can filter the customer list to that cluster
  useEffect(() => {
    if (!assigning) return;
    if (assignSegmentFilter === "__all__") {
      setSegmentMemberIds([]);
      return;
    }
    let cancelled = false;
    api.get(`/segments/${assignSegmentFilter}/customers`)
      .then(({ data }) => { if (!cancelled) setSegmentMemberIds(data.map((c) => c.id)); })
      .catch(() => { if (!cancelled) setSegmentMemberIds([]); });
    return () => { cancelled = true; };
  }, [assignSegmentFilter, assigning]);

  // Customers to show in Assegna dialog: honour branch + segment filters and the coupon's own branch scope
  const assignableCustomers = useMemo(() => {
    if (!assigning) return [];
    const couponBranches = assigning.branch_ids || [];
    return customers.filter((c) => {
      if (couponBranches.length > 0 && !couponBranches.includes(c.branch_id)) return false;
      if (assignBranchFilter !== "__all__" && c.branch_id !== assignBranchFilter) return false;
      if (assignSegmentFilter !== "__all__" && !segmentMemberIds.includes(c.id)) return false;
      return true;
    });
  }, [assigning, customers, assignBranchFilter, assignSegmentFilter, segmentMemberIds]);

  const addAllVisible = () => {
    const ids = assignableCustomers.map((c) => c.id);
    setSelectedIds(Array.from(new Set([...selectedIds, ...ids])));
    toast.success(`${ids.length} clienti aggiunti alla selezione`);
  };

  const doAssign = async () => {
    if (selectedIds.length === 0 && assignSegmentFilter === "__all__") {
      return toast.error("Seleziona almeno un cliente o un cluster");
    }
    const payload = {
      customer_ids: selectedIds,
      segment_ids: assignSegmentFilter !== "__all__" && selectedIds.length === 0 ? [assignSegmentFilter] : [],
    };
    const label = payload.segment_ids.length
      ? `al cluster "${segments.find((s) => s.id === payload.segment_ids[0])?.name}" `
      : `a ${selectedIds.length} clienti`;
    if (!window.confirm(`Assegnare il coupon "${assigning.name}" (${assigning.code}) ${label}?`)) return;
    try {
      const { data } = await api.post(`/coupons/${assigning.id}/assign`, payload);
      toast.success(`Assegnato a ${data.assigned} clienti (${data.resolved} risolti)`);
      setAssigning(null);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openBroadcast = (c) => {
    setBroadcastFor(c);
    setBroadcastBody(`Ciao {{name}}! Solo per te il coupon ${c.code}: ${c.discount_type === "percent" ? `-${c.discount_value}%` : `-€ ${c.discount_value}`} valido fino al {{coupon_expiry}}. Ti aspettiamo!`);
    setSelectedIds([]);
    setBroadcastPreview(null);
  };

  useEffect(() => {
    if (!broadcastFor) return;
    let cancelled = false;
    setBroadcastPreviewLoading(true);
    api.post("/notifications/broadcast/preview", {
      customer_ids: selectedIds.length ? selectedIds : null,
      coupon_id: broadcastFor.id,
    })
      .then(({ data }) => { if (!cancelled) setBroadcastPreview(data); })
      .catch(() => { if (!cancelled) setBroadcastPreview(null); })
      .finally(() => { if (!cancelled) setBroadcastPreviewLoading(false); });
    return () => { cancelled = true; };
  }, [broadcastFor, selectedIds]);

  const doBroadcast = async () => {
    const target = broadcastPreview?.eligible ?? (selectedIds.length || customers.length);
    const excluded = broadcastPreview?.excluded_wallet || 0;
    const extra = excluded > 0 ? ` (${excluded} esclusi perché non-Wallet)` : "";
    if (!window.confirm(
      `Confermi l'invio del coupon "${broadcastFor.name}" a ${target} clienti via WhatsApp?${extra}`
    )) return;
    try {
      const { data } = await api.post("/notifications/broadcast", {
        body: broadcastBody,
        customer_ids: selectedIds.length ? selectedIds : null,
        coupon_id: broadcastFor.id,
      });
      toast.success(`${data.count} messaggi pronti · apri WhatsApp per ognuno`);
      setBroadcastFor(null);
      setWaQueue({ messages: data.messages });
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleId = (id) => setSelectedIds((prev) => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const allBroadcastSelected = useMemo(() => customers.length > 0 && selectedIds.length === customers.length, [customers, selectedIds]);
  const allAssignVisibleSelected = useMemo(
    () => assignableCustomers.length > 0 && assignableCustomers.every((c) => selectedIds.includes(c.id)),
    [assignableCustomers, selectedIds]
  );

  return (
    <div className="space-y-6 animate-fade-up" data-testid="coupons-page">
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <div>
          <span className="pill pill-red"><Ticket className="h-3 w-3" /> COUPON</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold mt-3">Coupon sconto</h1>
          <p className="text-zinc-400 mt-1">Crea coupon a valore o percentuale, limita a una o più sedi e assegnali a clienti o cluster.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-start">
          <Button data-testid="coupon-expiry-reminder" variant="outline" onClick={runExpiryReminder} disabled={reminderBusy} className="h-11 border-amber-500/40 text-amber-700 hover:bg-amber-50" title="Invia subito push + WhatsApp ai clienti con coupon non usati che scadono domani (il job automatico gira alle 08:00)">
            <BellRing className="h-4 w-4 mr-2" /> {reminderBusy ? "Invio…" : "Invia promemoria scadenza ora"}
          </Button>
          <Button data-testid="coupon-new" onClick={openNew} className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)] h-11">
            <Plus className="h-4 w-4 mr-2" /> Nuovo coupon
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {coupons.length === 0 && <div className="text-zinc-500 col-span-full">Nessun coupon creato.</div>}
        {coupons.map((c) => (
          <div key={c.id} className="card-elev p-0 overflow-hidden flex flex-col gap-3" data-testid={`coupon-${c.id}`}>
            {c.image_url && (
              <div className="w-full h-32 bg-black overflow-hidden">
                <img src={resolveImageUrl(c.image_url)} alt={c.name} className="w-full h-full object-cover" data-testid={`coupon-img-${c.id}`} />
              </div>
            )}
            <div className="p-5 flex flex-col gap-3">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-xl bg-red-500/10 text-red-300 flex items-center justify-center">
                  {c.discount_type === "percent" ? <Percent className="h-5 w-5" /> : <Euro className="h-5 w-5" />}
                </div>
                <div>
                  <div className="font-heading font-bold text-lg">{c.name}</div>
                  <div className="text-xs text-zinc-500 font-mono">{c.code}</div>
                </div>
              </div>
              <span className={`pill ${c.active ? "pill-mint" : "pill-muted"}`}>{c.active ? "Attivo" : "Off"}</span>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm border-t border-zinc-900 pt-3">
              <div>
                <div className="text-[10px] uppercase text-zinc-500 tracking-widest">Sconto</div>
                <div className="metric-value text-2xl text-amber-300">
                  {c.discount_type === "percent" ? `${c.discount_value}%` : `€ ${c.discount_value}`}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-zinc-500 tracking-widest">Spesa minima</div>
                <div className="font-mono">€ {(c.min_spend || 0).toFixed(2)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-zinc-500 tracking-widest">Valido fino</div>
                <div className="text-xs">{c.valid_until || "senza scadenza"}</div>
              </div>
              {c.stats && (
                <div className="col-span-2 rounded-lg bg-zinc-950 border border-zinc-800 px-3 py-2 flex flex-wrap gap-x-4 gap-y-1 text-xs" data-testid={`coupon-stats-${c.id}`}>
                  <span><span className="text-zinc-500">Assegnati</span> <b className="font-mono" data-testid={`coupon-stats-assigned-${c.id}`}>{c.stats.assigned}</b></span>
                  <span><span className="text-zinc-500">Usati</span> <b className="font-mono" data-testid={`coupon-stats-used-${c.id}`}>{c.stats.used}</b></span>
                  <span className="inline-flex items-center gap-1"><BellRing className="h-3 w-3 text-amber-600" /><span className="text-zinc-500">Promemoria</span> <b className="font-mono" data-testid={`coupon-stats-reminded-${c.id}`}>{c.stats.reminded}</b>{c.stats.reminded_wa > 0 && <span className="text-zinc-500">({c.stats.reminded_wa} WA)</span>}</span>
                  {c.stats.reminded > 0 && (
                    <span><span className="text-zinc-500">Usati dopo promemoria</span> <b className="font-mono text-emerald-700" data-testid={`coupon-stats-reminded-used-${c.id}`}>{c.stats.reminded_used}</b> <span className="text-zinc-500">({Math.round((c.stats.reminded_used / c.stats.reminded) * 100)}%)</span></span>
                  )}
                </div>
              )}
              <div>
                <div className="text-[10px] uppercase text-zinc-500 tracking-widest">Utilizzo</div>
                <span className={`pill ${c.single_use ? "pill-red" : "pill-mint"} text-[10px]`}>
                  {c.single_use ? "Univoco · 1 uso" : "Ripetibile"}
                </span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {c.wallet_only && (
                    <span className="pill pill-gold text-[10px] flex items-center gap-0.5" data-testid={`coupon-wallet-only-${c.id}`}>
                      <Wallet className="h-3 w-3" /> Solo Wallet
                    </span>
                  )}
                  {c.allowed_weekdays && c.allowed_weekdays.length > 0 && (
                    <span className="pill pill-mint text-[10px]" data-testid={`coupon-weekdays-${c.id}`}>
                      {c.allowed_weekdays.map(d => ["Lun","Mar","Mer","Gio","Ven","Sab","Dom"][d]).join(",")}
                    </span>
                  )}
                  {c.allowed_hour_start != null && c.allowed_hour_end != null && (
                    <span className="pill pill-red text-[10px]" data-testid={`coupon-hours-${c.id}`}>
                      {String(c.allowed_hour_start).padStart(2,"0")}–{String(c.allowed_hour_end).padStart(2,"0")}
                    </span>
                  )}
                </div>
              </div>
            </div>
            {/* Branch scope badges */}
            <div className="flex flex-wrap gap-1 border-t border-zinc-900 pt-2" data-testid={`coupon-branches-${c.id}`}>
              <span className="text-[10px] uppercase text-zinc-500 tracking-widest mr-1 self-center">Sedi:</span>
              {(!c.branch_ids || c.branch_ids.length === 0) && (
                <span className="pill pill-muted text-[10px]">Tutte le sedi</span>
              )}
              {(c.branch_ids || []).map((bid) => (
                <span key={bid} className="pill text-[10px]" style={{ background: "rgba(245,158,11,0.15)", color: "#FCD34D", border: "1px solid rgba(245,158,11,0.35)" }}>
                  <Store className="h-3 w-3" /> {branchName(bid)}
                </span>
              ))}
            </div>
            <div className="flex gap-2 pt-1">
              <Button data-testid={`coupon-assign-${c.id}`} size="sm" variant="outline" onClick={() => openAssign(c)} className="border-zinc-800 flex-1">
                <UserPlus className="h-3.5 w-3.5 mr-1" /> Assegna
              </Button>
              <Button data-testid={`coupon-send-${c.id}`} size="sm" onClick={() => openBroadcast(c)} className="bg-[color:var(--whatsapp)] text-black hover:opacity-90 flex-1">
                <Send className="h-3.5 w-3.5 mr-1" /> Invia
              </Button>
              <Button size="sm" variant="outline" onClick={() => openEdit(c)} className="border-zinc-800"><Pencil className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="outline" onClick={() => remove(c.id)} className="border-zinc-800 hover:border-red-500 hover:text-red-300"><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
            </div>
          </div>
        ))}
      </div>

      {/* NEW/EDIT */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-h-[90dvh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifica coupon" : "Nuovo coupon"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex items-end justify-between gap-2">
              <div className="flex-1">
                <Label className="text-zinc-300 mb-1 inline-block">Nome</Label>
                <Input data-testid="coupon-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-950 border-zinc-800" />
              </div>
              <AiHelper
                preset="coupon_copy"
                contextLabel="Genera copy coupon con AI"
                hint="Es. sconto benvenuto -10%, valido 30 giorni, tono caldo"
                triggerLabel="AI Copy"
                testid="coupons-ai-copy"
                onApply={(v) => {
                  if (typeof v === "object" && v) {
                    setForm((prev) => ({
                      ...prev,
                      name: v.name || prev.name,
                      // il push_message viene mostrato come toast di conferma
                    }));
                    if (v.push_message) toast.success(`Suggerimento push: ${v.push_message}`, { duration: 8000 });
                  }
                }}
              />
            </div>
            <div><Label className="text-zinc-300 mb-1 inline-block">Codice (opzionale, generato se vuoto)</Label>
              <Input data-testid="coupon-form-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="bg-zinc-950 border-zinc-800 font-mono uppercase" />
            </div>

            {/* Immagine esplicativa dell'iniziativa: upload manuale o generazione AI */}
            <div className="p-3 rounded-lg bg-zinc-950 border border-zinc-800 space-y-2" data-testid="coupon-form-image">
              <div className="flex items-center justify-between">
                <Label className="text-zinc-300 flex items-center gap-1 text-sm"><ImageIcon className="h-3.5 w-3.5" /> Immagine iniziativa (opzionale)</Label>
                {form.image_url && (
                  <img src={resolveImageUrl(form.image_url)} alt="" className="h-10 w-10 rounded-md object-cover border border-zinc-800" />
                )}
              </div>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                data-testid="coupon-form-image-input"
                onChange={async (e) => {
                  const f = e.target.files?.[0]; if (!f) return;
                  if (f.size > 5 * 1024 * 1024) { toast.error("Immagine troppo grande (max 5MB)"); return; }
                  setUploadingImage(true);
                  try {
                    const fd = new FormData(); fd.append("file", f); fd.append("purpose", "coupon");
                    const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    setForm((p) => ({ ...p, image_url: data.url }));
                    toast.success("Immagine caricata");
                  } catch (err) { toast.error(formatApiError(err)); }
                  finally { setUploadingImage(false); e.target.value = ""; }
                }}
              />
              <div className="flex gap-2 flex-wrap">
                <Button
                  type="button" variant="outline" size="sm"
                  className="border-zinc-800"
                  onClick={() => imageInputRef.current?.click()}
                  disabled={uploadingImage}
                  data-testid="coupon-form-image-upload"
                >
                  <Upload className="h-3.5 w-3.5 mr-1" /> {uploadingImage ? "Caricamento…" : (form.image_url ? "Sostituisci" : "Carica immagine")}
                </Button>
                <AiImage
                  triggerLabel="Genera con AI"
                  testid="coupon-form-ai-image"
                  defaultPrompt={form.name ? `Immagine promozionale per coupon "${form.name}". Stile flat, colori caldi, senza testo.` : ""}
                  promptHint="Es. Sconto benvenuto stile lifestyle"
                  onGenerated={(url) => setForm((p) => ({ ...p, image_url: url }))}
                />
                {form.image_url && (
                  <Button
                    type="button" variant="outline" size="sm"
                    className="border-red-500/40 text-red-300 hover:bg-red-500/10"
                    onClick={() => setForm((p) => ({ ...p, image_url: "" }))}
                    data-testid="coupon-form-image-remove"
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Rimuovi
                  </Button>
                )}
              </div>
              <p className="text-[11px] text-zinc-500">L'immagine appare sul portale cliente e sullo scontrino insieme al coupon.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-zinc-300 mb-1 inline-block">Tipo sconto</Label>
                <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                  <SelectTrigger data-testid="coupon-form-type" className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800">
                    <SelectItem value="percent">Percentuale</SelectItem>
                    <SelectItem value="value">Valore €</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-zinc-300 mb-1 inline-block">Valore</Label>
                <Input data-testid="coupon-form-value" type="number" step="0.01" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: parseFloat(e.target.value || "0") })} className="bg-zinc-950 border-zinc-800" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-zinc-300 mb-1 inline-block">Spesa minima</Label>
                <Input data-testid="coupon-form-min" type="number" step="0.01" value={form.min_spend} onChange={(e) => setForm({ ...form, min_spend: parseFloat(e.target.value || "0") })} className="bg-zinc-950 border-zinc-800" />
              </div>
              <div><Label className="text-zinc-300 mb-1 inline-block">Valido da</Label>
                <DatePickerInput testid="coupon-form-from" value={form.valid_from || ""} max={form.valid_until || undefined} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} />
              </div>
              <div><Label className="text-zinc-300 mb-1 inline-block">Valido fino</Label>
                <DatePickerInput testid="coupon-form-to" value={form.valid_until || ""} min={form.valid_from || undefined} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} />
              </div>
            </div>

            {/* Branch scope */}
            <div className="p-3 rounded-lg bg-zinc-950 border border-zinc-800 space-y-2" data-testid="coupon-form-branches">
              <Label className="text-zinc-300 text-sm flex items-center gap-1"><Store className="h-3 w-3" /> Sedi in cui è valido</Label>
              <p className="text-[11px] text-zinc-500 -mt-1">
                {(form.branch_ids || []).length === 0
                  ? "Nessuna sede selezionata = valido in tutte le sedi"
                  : `Valido solo in: ${form.branch_ids.map(branchName).join(", ")}`}
              </p>
              <div className="flex flex-wrap gap-2">
                {branches.length === 0 && <div className="text-xs text-zinc-500">Nessuna sede configurata.</div>}
                {branches.map((b) => {
                  const on = (form.branch_ids || []).includes(b.id);
                  return (
                    <button
                      key={b.id}
                      type="button"
                      data-testid={`coupon-form-branch-${b.id}`}
                      onClick={() => toggleBranch(b.id)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${on ? "bg-amber-500/20 border-amber-500/60 text-amber-100" : "bg-zinc-950 border-zinc-800 text-zinc-400 hover:border-zinc-700"}`}
                    >
                      {on ? "✓ " : ""}{b.name}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-950 border border-zinc-800">
              <div><div className="text-sm font-medium">Attivo</div></div>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-950 border border-zinc-800">
              <div>
                <div className="text-sm font-medium">Utilizzo univoco (single-use)</div>
                <div className="text-xs text-zinc-500">
                  {form.single_use
                    ? "Il cliente può usarlo una sola volta, poi si annulla automaticamente"
                    : "Riutilizzabile: il cliente può usarlo più volte finché il periodo promozionale è valido"}
                </div>
              </div>
              <Switch data-testid="coupon-form-singleuse" checked={form.single_use} onCheckedChange={(v) => setForm({ ...form, single_use: v })} />
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-black border border-amber-500/40">
              <div>
                <div className="text-sm font-medium flex items-center gap-2 text-amber-300">
                  <Wallet className="h-4 w-4" /> Esclusivo Wallet
                </div>
                <div className="text-xs text-zinc-500">
                  {form.wallet_only
                    ? "Solo i clienti che hanno salvato la carta nel Wallet possono riscattarlo — incentiva l'adozione"
                    : "Riscattabile da tutti i clienti"}
                </div>
              </div>
              <Switch data-testid="coupon-form-wallet-only" checked={form.wallet_only} onCheckedChange={(v) => setForm({ ...form, wallet_only: v })} />
            </div>

            {/* Bundle rules: weekdays + hour window */}
            <div className="p-3 rounded-lg bg-zinc-950 border border-zinc-800 space-y-3">
              <div>
                <div className="text-sm font-medium mb-2">Giorni della settimana</div>
                <div className="text-xs text-zinc-500 mb-2">
                  {(form.allowed_weekdays || []).length === 0
                    ? "Valido tutti i giorni · Nessun filtro"
                    : `Valido solo: ${(form.allowed_weekdays || []).map(d => ["Lun","Mar","Mer","Gio","Ven","Sab","Dom"][d]).join(", ")}`}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {["Lun","Mar","Mer","Gio","Ven","Sab","Dom"].map((l, i) => {
                    const active = (form.allowed_weekdays || []).includes(i);
                    return (
                      <button
                        key={i}
                        data-testid={`coupon-form-wd-${i}`}
                        type="button"
                        onClick={() => toggleWeekday(i)}
                        className={`py-1.5 text-[11px] rounded ${active ? "bg-amber-500 text-black font-semibold" : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800"}`}
                      >{l}</button>
                    );
                  })}
                </div>
              </div>
              <div>
                <div className="text-sm font-medium mb-2">Fascia oraria (opzionale)</div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-zinc-500">Dalle</span>
                  <Input
                    type="number" min="0" max="23"
                    data-testid="coupon-form-hour-start"
                    value={form.allowed_hour_start ?? ""}
                    onChange={(e) => setForm({ ...form, allowed_hour_start: e.target.value === "" ? null : parseInt(e.target.value) })}
                    className="bg-zinc-950 border-zinc-800 w-20 text-center"
                  />
                  <span className="text-zinc-500">alle</span>
                  <Input
                    type="number" min="0" max="23"
                    data-testid="coupon-form-hour-end"
                    value={form.allowed_hour_end ?? ""}
                    onChange={(e) => setForm({ ...form, allowed_hour_end: e.target.value === "" ? null : parseInt(e.target.value) })}
                    className="bg-zinc-950 border-zinc-800 w-20 text-center"
                  />
                  <span className="text-zinc-500">·</span>
                  <button type="button" onClick={() => setForm({ ...form, allowed_hour_start: null, allowed_hour_end: null })} className="text-zinc-500 hover:text-white underline">reset</button>
                </div>
                <div className="text-[11px] text-zinc-500 mt-1">
                  Es. 18-23 = happy hour. Lascia vuoto per validità 24/7.
                </div>
              </div>
            </div>

            <Button data-testid="coupon-form-submit" onClick={submit} className="w-full bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]">
              {editing ? "Salva" : "Crea coupon"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ASSIGN */}
      <Dialog open={!!assigning} onOpenChange={(o) => !o && setAssigning(null)}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assegna coupon a clienti</DialogTitle>
          </DialogHeader>
          {assigning && (
            <div className="space-y-3">
              {(assigning.branch_ids || []).length > 0 && (
                <div className="text-[11px] text-amber-300/90 bg-amber-500/10 border border-amber-500/30 rounded-md p-2" data-testid="assign-branch-note">
                  Questo coupon è ristretto a: <strong>{assigning.branch_ids.map(branchName).join(", ")}</strong>. Solo i clienti di queste sedi possono riceverlo.
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-zinc-400 text-[11px] uppercase tracking-widest">Sede</Label>
                  <Select value={assignBranchFilter} onValueChange={setAssignBranchFilter}>
                    <SelectTrigger data-testid="assign-branch-filter" className="bg-zinc-950 border-zinc-800 h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800">
                      <SelectItem value="__all__">
                        {(assigning.branch_ids || []).length > 0 ? "Tutte le sedi consentite" : "Tutte le sedi"}
                      </SelectItem>
                      {((assigning.branch_ids || []).length > 0 ? branches.filter((b) => assigning.branch_ids.includes(b.id)) : branches).map((b) => (
                        <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-400 text-[11px] uppercase tracking-widest">Cluster</Label>
                  <Select value={assignSegmentFilter} onValueChange={setAssignSegmentFilter}>
                    <SelectTrigger data-testid="assign-segment-filter" className="bg-zinc-950 border-zinc-800 h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800">
                      <SelectItem value="__all__">Tutti i clienti</SelectItem>
                      {segments.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name} ({s.member_count ?? "?"})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center justify-between text-xs text-zinc-400 pt-1">
                <span data-testid="assign-visible-count">
                  <Users className="h-3 w-3 inline mr-1" /> {assignableCustomers.length} clienti filtrati · {selectedIds.length} selezionati
                </span>
                <button
                  type="button"
                  data-testid="assign-add-all"
                  onClick={addAllVisible}
                  disabled={assignableCustomers.length === 0}
                  className="text-amber-300 hover:text-amber-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Aggiungi tutti ({assignableCustomers.length})
                </button>
              </div>

              <div className="space-y-1 max-h-80 overflow-y-auto rounded-md border border-zinc-800 bg-zinc-950 p-2">
                <label className="flex items-center gap-2 p-2 border-b border-zinc-800">
                  <Checkbox
                    checked={allAssignVisibleSelected}
                    onCheckedChange={(v) => {
                      const ids = assignableCustomers.map((c) => c.id);
                      if (v) setSelectedIds(Array.from(new Set([...selectedIds, ...ids])));
                      else setSelectedIds(selectedIds.filter((id) => !ids.includes(id)));
                    }}
                  />
                  <span className="text-sm font-semibold">Seleziona tutti i visibili ({assignableCustomers.length})</span>
                </label>
                {assignableCustomers.length === 0 && (
                  <div className="p-4 text-center text-xs text-zinc-500">Nessun cliente corrisponde ai filtri.</div>
                )}
                {assignableCustomers.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 p-2 hover:bg-zinc-900 rounded-md cursor-pointer" data-testid={`assign-cust-${c.id}`}>
                    <Checkbox checked={selectedIds.includes(c.id)} onCheckedChange={() => toggleId(c.id)} />
                    <div className="flex-1">
                      <div className="text-sm">{c.name}</div>
                      <div className="text-xs text-zinc-500 font-mono">{c.card_number} · {c.phone}</div>
                    </div>
                    {c.branch_id && (
                      <span className="pill pill-muted text-[9px]">{branchName(c.branch_id)}</span>
                    )}
                  </label>
                ))}
              </div>

              <Button
                data-testid="assign-submit"
                onClick={doAssign}
                disabled={selectedIds.length === 0 && assignSegmentFilter === "__all__"}
                className="w-full bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)] disabled:opacity-50"
              >
                {assignSegmentFilter !== "__all__" && selectedIds.length === 0
                  ? `Assegna a tutto il cluster "${segments.find((s) => s.id === assignSegmentFilter)?.name}"`
                  : `Assegna a ${selectedIds.length} cliente/i`}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* BROADCAST */}
      <Dialog open={!!broadcastFor} onOpenChange={(o) => !o && setBroadcastFor(null)}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-h-[90dvh] overflow-y-auto">
          <DialogHeader><DialogTitle>Invia coupon via WhatsApp</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {broadcastFor && (broadcastFor.branch_ids || []).length > 0 && (
              <div className="text-[11px] text-amber-300/90 bg-amber-500/10 border border-amber-500/30 rounded-md p-2">
                Il coupon è ristretto a <strong>{broadcastFor.branch_ids.map(branchName).join(", ")}</strong>: i destinatari fuori sede sono esclusi automaticamente.
              </div>
            )}
            <div>
              <Label className="text-zinc-300 mb-1 inline-block">Messaggio</Label>
              <textarea
                data-testid="broadcast-body"
                value={broadcastBody}
                onChange={(e) => setBroadcastBody(e.target.value)}
                rows={4}
                className="w-full rounded-md p-2 bg-zinc-950 border border-zinc-800 text-sm"
              />
              <div className="text-[11px] text-zinc-500 mt-1">
                Variabili: <span className="font-mono">{"{{name}}"}</span>, <span className="font-mono">{"{{points}}"}</span>, <span className="font-mono">{"{{card}}"}</span>, <span className="font-mono">{"{{coupon}}"}</span>, <span className="font-mono">{"{{coupon_expiry}}"}</span>, <span className="font-mono">{"{{discount}}"}</span>
              </div>
            </div>
            <div>
              <Label className="text-zinc-300 mb-1 inline-block">Destinatari (vuoto = tutti i clienti attivi)</Label>
              <div className="max-h-64 overflow-y-auto rounded-md border border-zinc-800 p-2 bg-zinc-950 space-y-1">
                <label className="flex items-center gap-2 p-1">
                  <Checkbox checked={allBroadcastSelected} onCheckedChange={(v) => setSelectedIds(v ? customers.map(c => c.id) : [])} />
                  <span className="text-xs font-semibold">Tutti ({customers.length})</span>
                </label>
                {customers.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 p-1 text-xs">
                    <Checkbox checked={selectedIds.includes(c.id)} onCheckedChange={() => toggleId(c.id)} />
                    <span>{c.name} · <span className="text-zinc-500 font-mono">{c.phone}</span></span>
                  </label>
                ))}
              </div>
            </div>
            <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3 flex items-center justify-between gap-2" data-testid="broadcast-preview-panel">
              <div className="text-xs text-zinc-400">Destinatari stimati</div>
              {broadcastPreviewLoading ? (
                <div className="text-xs text-zinc-500">Calcolo…</div>
              ) : broadcastPreview ? (
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  <span className="pill pill-mint text-[11px]" data-testid="broadcast-preview-eligible">
                    {broadcastPreview.eligible} destinatari
                  </span>
                  {broadcastPreview.wallet_only_filter && broadcastPreview.excluded_wallet > 0 && (
                    <span className="pill pill-gold text-[11px]" data-testid="broadcast-preview-excluded">
                      Solo Wallet: −{broadcastPreview.excluded_wallet} esclusi
                    </span>
                  )}
                  {broadcastPreview.branch_scope && broadcastPreview.branch_scope.length > 0 && (
                    <span className="pill text-[11px]" style={{ background: "rgba(245,158,11,0.15)", color: "#FCD34D", border: "1px solid rgba(245,158,11,0.35)" }}>
                      Solo sedi: {broadcastPreview.branch_scope.map(branchName).join(", ")}
                    </span>
                  )}
                </div>
              ) : (
                <div className="text-xs text-zinc-500">—</div>
              )}
            </div>
            <Button
              data-testid="broadcast-submit"
              onClick={doBroadcast}
              disabled={broadcastPreviewLoading || (broadcastPreview && broadcastPreview.eligible === 0)}
              className="w-full bg-[color:var(--whatsapp)] text-black hover:opacity-90 disabled:opacity-50"
            >
              <Send className="h-4 w-4 mr-2" />
              Invia a {broadcastPreview?.eligible ?? "…"} clienti (assegna + apre wa.me)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <WaSendQueue open={!!waQueue} onClose={() => setWaQueue(null)} messages={waQueue?.messages || []} />
    </div>
  );
}
